import {
  CanvasTexture,
  MeshStandardMaterial,
  RepeatWrapping,
  SRGBColorSpace,
} from 'three';
import { createRandom } from '../../../utils/random.js';

export const CITY_PALETTE = {
  wall: ['#c9b08b', '#d7c5a4', '#bba17e', '#c2a07b', '#d8c9ae', '#b49574'],
  wallTrim: ['#8f775b', '#a08b70', '#7e6852'],
  house: ['#d8c7a9', '#cbb58f', '#b99b74', '#d2b68b', '#b68762', '#c7a681'],
  roof: ['#8d806c', '#a2927a', '#776b5b', '#b0a086'],
  metal: '#4c4f50',
  tire: '#18191a',
  green: '#4b6b3d',
  dryGreen: '#748153',
  dustyGreen: '#5d6f45',
  concrete: '#b4ab99',
  curb: '#c8c1b2',
  asphalt: '#3d3c39',
  paint: '#e8e2d2',
  dirt: '#a47d58',
  dirtLight: '#b89166',
  earthRed: '#8f6444',
};

export function createCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

export function createAsphaltTexture(anisotropy = 4) {
  const size = 512;
  const canvas = createCanvas(size, size);
  const ctx = canvas.getContext('2d');
  const random = createRandom(71);

  ctx.fillStyle = '#45433f';
  ctx.fillRect(0, 0, size, size);

  for (let i = 0; i < 7000; i += 1) {
    const shade = 48 + Math.floor(random() * 42);
    ctx.fillStyle = `rgba(${shade}, ${shade - 1}, ${shade - 3}, ${random.range(0.15, 0.55)})`;
    const radius = random.range(0.4, 1.8);
    ctx.fillRect(random() * size, random() * size, radius, radius);
  }

  // Faded repair patches and tar seams.
  ctx.globalAlpha = 0.16;
  for (let i = 0; i < 8; i += 1) {
    ctx.fillStyle = random() > 0.5 ? '#2f2e2b' : '#55514b';
    ctx.fillRect(random() * size, random() * size, random.range(20, 100), random.range(14, 70));
  }
  ctx.globalAlpha = 0.28;
  ctx.strokeStyle = '#292826';
  ctx.lineWidth = 2;
  for (let i = 0; i < 7; i += 1) {
    ctx.beginPath();
    const x = random() * size;
    const y = random() * size;
    ctx.moveTo(x, y);
    ctx.bezierCurveTo(
      x + random.range(-45, 45),
      y + random.range(15, 70),
      x + random.range(-40, 40),
      y + random.range(50, 120),
      x + random.range(-60, 60),
      y + random.range(90, 180),
    );
    ctx.stroke();
  }

  return finishTexture(canvas, anisotropy);
}

export function createDirtTexture({ repeat = 1, anisotropy = 4, seed = 12 } = {}) {
  const size = 512;
  const canvas = createCanvas(size, size);
  const ctx = canvas.getContext('2d');
  const random = createRandom(seed);

  ctx.fillStyle = '#a9825e';
  ctx.fillRect(0, 0, size, size);

  for (let i = 0; i < 9000; i += 1) {
    const warm = random.range(-18, 18);
    ctx.fillStyle = `rgba(${165 + warm}, ${128 + warm}, ${94 + warm}, ${random.range(0.08, 0.32)})`;
    const radius = random.range(0.5, 2.2);
    ctx.fillRect(random() * size, random() * size, radius, radius);
  }

  // Broad, faint vehicle tracks.
  ctx.globalAlpha = 0.09;
  ctx.strokeStyle = '#6f523c';
  ctx.lineWidth = 18;
  for (let i = 0; i < 5; i += 1) {
    ctx.beginPath();
    const y = random() * size;
    ctx.moveTo(-20, y);
    ctx.bezierCurveTo(size * 0.3, y + random.range(-18, 18), size * 0.7, y + random.range(-20, 20), size + 20, y + random.range(-14, 14));
    ctx.stroke();
  }

  const texture = finishTexture(canvas, anisotropy);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.repeat.set(repeat, repeat);
  return texture;
}

export function createConcreteTexture(anisotropy = 4) {
  const size = 512;
  const canvas = createCanvas(size, size);
  const ctx = canvas.getContext('2d');
  const random = createRandom(33);

  ctx.fillStyle = '#b8ae9d';
  ctx.fillRect(0, 0, size, size);

  for (let i = 0; i < 4200; i += 1) {
    const shade = 140 + Math.floor(random() * 55);
    ctx.fillStyle = `rgba(${shade}, ${shade - 5}, ${shade - 14}, ${random.range(0.06, 0.25)})`;
    const radius = random.range(0.5, 2);
    ctx.fillRect(random() * size, random() * size, radius, radius);
  }

  ctx.strokeStyle = 'rgba(86, 79, 68, 0.32)';
  ctx.lineWidth = 2;
  for (let i = 0; i < size; i += 128) {
    ctx.beginPath();
    ctx.moveTo(0, i);
    ctx.lineTo(size, i);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i, size);
    ctx.stroke();
  }

  return finishTexture(canvas, anisotropy);
}

export function createMaterials(renderer) {
  const anisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), 8);

  const asphaltTexture = createAsphaltTexture(anisotropy);
  const dirtTexture = createDirtTexture({ anisotropy });
  const dirtGroundTexture = createDirtTexture({ anisotropy, repeat: 280, seed: 18 });
  const concreteTexture = createConcreteTexture(anisotropy);

  // Box geometries use 0..1 UVs, so the wide road slabs need their own tiled
  // texture instances rather than the metre-based ribbon UVs.
  const dirtStreetTexture = dirtTexture.clone();
  dirtStreetTexture.repeat.set(30, 1.5);
  dirtStreetTexture.needsUpdate = true;

  const asphaltWideTexture = asphaltTexture.clone();
  asphaltWideTexture.repeat.set(70, 3);
  asphaltWideTexture.needsUpdate = true;

  return {
    asphalt: new MeshStandardMaterial({
      map: asphaltTexture,
      color: 0xffffff,
      roughness: 0.96,
      metalness: 0.01,
    }),
    dirt: new MeshStandardMaterial({
      map: dirtTexture,
      color: 0xffffff,
      roughness: 1,
      metalness: 0,
    }),
    dirtStreet: new MeshStandardMaterial({
      map: dirtStreetTexture,
      color: 0xffffff,
      roughness: 1,
      metalness: 0,
    }),
    dirtGround: new MeshStandardMaterial({
      map: dirtGroundTexture,
      color: 0xffffff,
      roughness: 1,
      metalness: 0,
    }),
    asphaltWide: new MeshStandardMaterial({
      map: asphaltWideTexture,
      color: 0xffffff,
      roughness: 0.96,
      metalness: 0.01,
    }),
    concrete: new MeshStandardMaterial({
      map: concreteTexture,
      color: 0xffffff,
      roughness: 0.92,
      metalness: 0.01,
    }),
    curb: new MeshStandardMaterial({
      color: CITY_PALETTE.curb,
      roughness: 0.9,
      metalness: 0.01,
    }),
    paint: new MeshStandardMaterial({
      color: CITY_PALETTE.paint,
      roughness: 0.58,
      metalness: 0.02,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    }),
    darkRepair: new MeshStandardMaterial({
      color: 0x3a3936,
      roughness: 0.95,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    }),
    glass: new MeshStandardMaterial({
      color: 0x1d2a30,
      roughness: 0.25,
      metalness: 0.4,
    }),
    tire: new MeshStandardMaterial({
      color: CITY_PALETTE.tire,
      roughness: 0.9,
      metalness: 0.02,
    }),
    metal: new MeshStandardMaterial({
      color: CITY_PALETTE.metal,
      roughness: 0.55,
      metalness: 0.55,
    }),
    whiteMetal: new MeshStandardMaterial({
      color: 0xd9d9d1,
      roughness: 0.5,
      metalness: 0.3,
    }),
    canopy: new MeshStandardMaterial({
      color: 0xf2f0e8,
      roughness: 0.62,
      metalness: 0.08,
    }),
    canopyLight: new MeshStandardMaterial({
      color: 0xfff6dc,
      emissive: 0xffe9b0,
      emissiveIntensity: 1.1,
      roughness: 0.4,
    }),
  };
}

export function createBoardTexture({
  width = 1024,
  height = 512,
  background = '#1d6b48',
  foreground = '#ffffff',
  accent = '#f0c04e',
  title = '',
  titleLatin = '',
  subtitle = '',
  border = true,
  fuelIcon = false,
  anisotropy = 8,
} = {}) {
  const canvas = createCanvas(width, height);
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width, height);

  if (border) {
    ctx.strokeStyle = foreground;
    ctx.lineWidth = Math.max(6, width * 0.012);
    ctx.strokeRect(width * 0.035, height * 0.055, width * 0.93, height * 0.89);
  }

  if (accent) {
    ctx.fillStyle = accent;
    ctx.fillRect(0, height - Math.max(12, height * 0.06), width, Math.max(12, height * 0.06));
    ctx.fillRect(0, 0, width, Math.max(8, height * 0.035));
  }

  const cx = width / 2;
  if (fuelIcon) {
    const iconX = width * 0.14;
    const iconY = height * 0.47;
    ctx.strokeStyle = foreground;
    ctx.lineWidth = Math.max(5, width * 0.012);
    ctx.strokeRect(iconX - width * 0.035, iconY - height * 0.16, width * 0.055, height * 0.3);
    ctx.strokeRect(iconX - width * 0.018, iconY - height * 0.22, width * 0.036, height * 0.07);
    ctx.beginPath();
    ctx.moveTo(iconX + width * 0.02, iconY - height * 0.12);
    ctx.lineTo(iconX + width * 0.065, iconY - height * 0.07);
    ctx.lineTo(iconX + width * 0.065, iconY + height * 0.09);
    ctx.stroke();
  }

  if (title) {
    const arabicSize = Math.round(height * (titleLatin ? 0.31 : 0.38));
    ctx.fillStyle = foreground;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.direction = 'rtl';
    ctx.font = `700 ${arabicSize}px "Geeza Pro", "Al Nile", "Noto Naskh Arabic", Arial, sans-serif`;
    ctx.fillText(title, cx, height * (titleLatin ? 0.38 : 0.47));
  }

  if (titleLatin) {
    ctx.fillStyle = accent || foreground;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.direction = 'ltr';
    ctx.font = `700 ${Math.round(height * 0.17)}px "Avenir Next", "Helvetica Neue", Arial, sans-serif`;
    ctx.fillText(titleLatin.toUpperCase(), cx, height * 0.66);
  }

  if (subtitle) {
    ctx.fillStyle = foreground;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.direction = 'ltr';
    ctx.font = `500 ${Math.round(height * 0.09)}px "Avenir Next", "Helvetica Neue", Arial, sans-serif`;
    ctx.fillText(subtitle, cx, height * (titleLatin ? 0.82 : 0.78));
  }

  return finishTexture(canvas, anisotropy);
}

export function createShopSignTexture(text, { background = '#efe4cf', foreground = '#24312b', accent = '#b73c2d' } = {}) {
  const canvas = createCanvas(512, 160);
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = background;
  ctx.fillRect(0, 0, 512, 160);
  ctx.fillStyle = accent;
  ctx.fillRect(0, 0, 16, 160);
  ctx.strokeStyle = 'rgba(30, 30, 30, 0.35)';
  ctx.lineWidth = 4;
  ctx.strokeRect(2, 2, 508, 156);

  ctx.fillStyle = foreground;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.direction = 'rtl';
  ctx.font = '700 72px "Geeza Pro", "Al Nile", "Noto Naskh Arabic", Arial, sans-serif';
  ctx.fillText(text, 256, 82);

  return finishTexture(canvas, 8);
}

/**
 * The rooftop board on LINAA TOWER. The real one is a tall white panel with the
 * Arabic name over the English, and a small mark above it — drawn rather than
 * modelled so the lettering stays legible from the road.
 */
export function createLinaaSignTexture() {
  const width = 620;
  const height = 700;
  const canvas = createCanvas(width, height);
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#f3f1ea';
  ctx.fillRect(0, 0, width, height);

  ctx.strokeStyle = '#cec8b8';
  ctx.lineWidth = 8;
  ctx.strokeRect(4, 4, width - 8, height - 8);

  const INK = '#173a30';

  // The little leaf mark over the name.
  ctx.save();
  ctx.translate(width / 2 - 26, 108);
  ctx.fillStyle = '#c08a2c';
  ctx.beginPath();
  ctx.ellipse(0, 0, 30, 17, -0.55, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#2f7d4f';
  ctx.beginPath();
  ctx.ellipse(16, -14, 22, 12, -0.85, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  ctx.fillStyle = INK;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.direction = 'rtl';
  ctx.font = '700 196px "Geeza Pro", "Al Nile", "Noto Naskh Arabic", Arial, sans-serif';
  ctx.fillText('ليناء', width / 2, 312);

  ctx.direction = 'ltr';
  ctx.font = '600 56px "Helvetica Neue", Helvetica, Arial, sans-serif';
  ctx.fillText('LINAA TOWER', width / 2, 520);

  ctx.fillStyle = '#c08a2c';
  ctx.fillRect(width / 2 - 150, 572, 300, 7);

  return finishTexture(canvas, 8);
}

/**
 * The rooftop lightbox on the GO tower. A white backlit face with the mark in
 * deep green, drawn rather than modelled so the lettering stays crisp from the
 * far end of the street.
 */
export function createGoSignTexture() {
  // The real lightbox carries a geometric double-ring monogram rather than
  // set type: a "G" with an open right side and a crossbar, locked against an
  // "O" that has a diagonal slash through it.
  const width = 1024;
  const height = 420;
  const canvas = createCanvas(width, height);
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#f2f3ec';
  ctx.fillRect(0, 0, width, height);

  // A backlit panel reads cool at the top and bottom edges in daylight.
  const shade = ctx.createLinearGradient(0, 0, 0, height);
  shade.addColorStop(0, 'rgba(206, 214, 208, 0.65)');
  shade.addColorStop(0.35, 'rgba(242, 243, 236, 0)');
  shade.addColorStop(1, 'rgba(198, 206, 200, 0.6)');
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, width, height);

  ctx.strokeStyle = '#c8c3b6';
  ctx.lineWidth = 9;
  ctx.strokeRect(5, 5, width - 10, height - 10);

  const GREEN = '#1d6b57';
  const cy = height / 2 - 4;
  const r = 146;
  const stroke = 52;
  const gx = 368;
  const ox = 660;

  ctx.strokeStyle = GREEN;
  ctx.fillStyle = GREEN;
  ctx.lineCap = 'butt';

  // G — a ring open at the upper right, closed off by a crossbar.
  ctx.lineWidth = stroke;
  ctx.beginPath();
  ctx.arc(gx, cy, r, (55 * Math.PI) / 180, Math.PI * 2);
  ctx.stroke();
  ctx.fillRect(gx - stroke / 2, cy - stroke / 2, r + stroke, stroke);

  // O — a full ring with a slash contained inside it.
  ctx.save();
  ctx.beginPath();
  ctx.arc(ox, cy, r, 0, Math.PI * 2);
  ctx.clip();
  ctx.beginPath();
  ctx.arc(ox, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.lineWidth = stroke * 0.72;
  ctx.beginPath();
  ctx.moveTo(ox - r, cy + r);
  ctx.lineTo(ox + r, cy - r);
  ctx.stroke();
  ctx.restore();

  // The thin rule along the bottom of the box.
  ctx.fillRect(190, height - 26, width - 380, 8);

  return finishTexture(canvas, 8);
}

export function createUTurnTexture() {
  const canvas = createCanvas(512, 512);
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#1b5fa8';
  ctx.beginPath();
  ctx.roundRect(14, 14, 484, 484, 40);
  ctx.fill();
  ctx.strokeStyle = '#f4f0e4';
  ctx.lineWidth = 14;
  ctx.beginPath();
  ctx.roundRect(40, 40, 432, 432, 26);
  ctx.stroke();

  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 38;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(330, 390);
  ctx.lineTo(330, 225);
  ctx.bezierCurveTo(330, 120, 180, 120, 180, 225);
  ctx.lineTo(180, 340);
  ctx.stroke();

  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.moveTo(180, 405);
  ctx.lineTo(132, 325);
  ctx.lineTo(228, 325);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.direction = 'ltr';
  ctx.font = '700 42px "Avenir Next", Arial, sans-serif';
  ctx.fillText('U-TURN', 256, 470);

  return finishTexture(canvas, 8);
}

export function createStreetSignTexture() {
  return createBoardTexture({
    width: 1400,
    height: 420,
    background: '#1f6043',
    foreground: '#f5f1e5',
    accent: '#e5bf62',
    title: 'شارع بشير النفيدي',
    titleLatin: 'Bashir Elnifidi Street · Street 60',
    subtitle: 'الخرطوم · KHARTOUM',
    border: true,
    anisotropy: 8,
  });
}

export function finishTexture(canvas, anisotropy) {
  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = anisotropy;
  texture.needsUpdate = true;
  return texture;
}
